import { Alert, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog, DialogModal, Empty } from "@zfp-akademie-a-s/zfp-design";
import { UsersIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import {
  getGetAnalysisQueryKey,
  getListCollaboratorsQueryKey,
  useListCollaborators,
  useRemoveCollaborator,
  useShareAnalysis,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { type AgentSummary, ShareAnalysisNotFoundErrorCode } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useContext, useState } from "react";
import { toast } from "sonner";
import { SHARING_ADVISORS_MAX_COUNT } from "../../constants";
import { AnalysisDetailContext } from "../AnalysisDetailContext";
import { SharingAdvisorItem } from "./SharingAdvisorItem";
import { SharingAdvisorSearch } from "./SharingAdvisorSearch";
import { SharingCurrentAdvisor } from "./SharingCurrentAdvisor";
import { TakeBackConfirmDialog } from "./TakeBackConfirmDialog";
import { getAgentFullName } from "./utils";

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly analysisName: string;
  readonly version: number;
  readonly sharedWithAgent: AgentSummary | null;
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px">
    {children}
  </Typography>
);

export const AnalysisSharingModal = ({ open, onClose, analysisName, version, sharedWithAgent }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const queryClient = useQueryClient();

  const { data: collaboratorList, isLoading, isError } = useListCollaborators({ query: { enabled: open } });
  const advisors = collaboratorList?.data ?? [];

  const { mutate: removeCollaborator, isPending: isRemoving } = useRemoveCollaborator({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCollaboratorsQueryKey() });
      },
      onError: () => {
        toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
      },
    },
  });

  const { mutate: shareAnalysis, isPending: isSharing } = useShareAnalysis({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId) });
        toast.success(t(Resources.AnalysisDetail.Sharing.Toast.Shared));
      },
      onError: (error, { data }) => {
        if (error?.code === ShareAnalysisNotFoundErrorCode.nsure_agent_not_found) {
          const advisor = advisors.find(({ userGuid }) => userGuid === data.userGuid);
          toast.error(
            t(Resources.AnalysisDetail.Sharing.Toast.ShareNsureAccountMissing, {
              advisorName: advisor ? getAgentFullName(advisor) : "",
            }),
          );
          return;
        }
        toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
      },
    },
  });

  const [advisorToShare, setAdvisorToShare] = useState<AgentSummary | null>(null);
  const [advisorToRemove, setAdvisorToRemove] = useState<AgentSummary | null>(null);
  const [isTakeBackConfirmOpen, setIsTakeBackConfirmOpen] = useState(false);

  const isMutating = isRemoving || isSharing;

  const handleShareConfirm = () => {
    if (!advisorToShare) return;
    shareAnalysis({ analysisId, versionId, data: { userGuid: advisorToShare.userGuid } }, { onSettled: () => setAdvisorToShare(null) });
  };

  const handleRemoveConfirm = () => {
    if (!advisorToRemove) return;
    removeCollaborator({ userGuid: advisorToRemove.userGuid }, { onSettled: () => setAdvisorToRemove(null) });
  };

  const shareDisabledReason = sharedWithAgent ? t(Resources.AnalysisDetail.Sharing.ShareDisabledTooltip) : undefined;

  const renderAdvisors = () => {
    if (isLoading) {
      return (
        <Stack spacing={Spacing.S}>
          <Skeleton variant="rounded" height={66} />
          <Skeleton variant="rounded" height={66} />
        </Stack>
      );
    }

    if (isError) {
      return <Alert severity="error">{t(Resources.AnalysisDetail.Sharing.LoadError)}</Alert>;
    }

    if (advisors.length === 0) {
      return (
        <Empty
          title={t(Resources.AnalysisDetail.Sharing.NoAdvisors.Title)}
          text={t(Resources.AnalysisDetail.Sharing.NoAdvisors.Text)}
          iconSize={40}
          icon={<UsersIcon sx={{ color: "grey.400" }} />}
        />
      );
    }

    return (
      <Stack spacing={Spacing.S}>
        <Stack spacing={Spacing.XS}>
          <SectionTitle>{t(Resources.AnalysisDetail.Sharing.SharedWith)}</SectionTitle>
          <Typography variant="caption" color="text.secondary">
            {t(Resources.AnalysisDetail.Sharing.SingleShareNote)}
          </Typography>
        </Stack>
        <Stack divider={<Divider />} overflow="auto" border={1} borderColor="divider" borderRadius="14px">
          {advisors.map((advisor) => (
            <SharingAdvisorItem
              key={advisor.userGuid}
              advisor={advisor}
              isShared={advisor.userGuid === sharedWithAgent?.userGuid}
              shareDisabledReason={shareDisabledReason}
              disabled={isMutating}
              onShare={() => setAdvisorToShare(advisor)}
              onRemove={() => setAdvisorToRemove(advisor)}
            />
          ))}
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <DialogModal
        open={open}
        onClose={onClose}
        closeLabel={t(Resources.Common.Close)}
        size="lg"
        title={t(Resources.AnalysisDetail.Sharing.Title)}
        subtitle={t(Resources.AnalysisDetail.Sharing.Subtitle, { analysisName, version })}
        content={
          <Stack spacing={Spacing.XXL}>
            <Stack spacing={Spacing.S}>
              <SectionTitle>{t(Resources.AnalysisDetail.Sharing.CurrentlySharedWith)}</SectionTitle>
              <SharingCurrentAdvisor
                sharedWithAgent={sharedWithAgent}
                disabled={isMutating}
                onTakeBack={() => setIsTakeBackConfirmOpen(true)}
              />
            </Stack>
            <Stack spacing={Spacing.S}>
              <SectionTitle>{t(Resources.AnalysisDetail.Sharing.AddAdvisor.Title)}</SectionTitle>
              {advisors.length >= SHARING_ADVISORS_MAX_COUNT ? (
                <Alert severity="warning">
                  {t(Resources.AnalysisDetail.Sharing.AddAdvisor.LimitReached, { max: SHARING_ADVISORS_MAX_COUNT })}
                </Alert>
              ) : (
                <SharingAdvisorSearch addedAdvisorSsoIds={advisors.map(({ ssoId }) => ssoId)} />
              )}
            </Stack>
            {renderAdvisors()}
          </Stack>
        }
      />
      <ConfirmDialog
        open={!!advisorToShare}
        title={t(Resources.AnalysisDetail.Sharing.Confirm.Share.Title)}
        message={t(Resources.AnalysisDetail.Sharing.Confirm.Share.Text, {
          advisorName: advisorToShare ? getAgentFullName(advisorToShare) : "",
          advisorId: advisorToShare?.ssoId ?? "",
        })}
        cancelLabel={t(Resources.Common.Cancel)}
        confirmLabel={t(Resources.AnalysisDetail.Sharing.Share)}
        onConfirm={handleShareConfirm}
        onCancel={() => setAdvisorToShare(null)}
        isPending={isSharing}
      />
      <ConfirmDialog
        open={!!advisorToRemove}
        title={t(Resources.AnalysisDetail.Sharing.Confirm.Remove.Title)}
        message={t(Resources.AnalysisDetail.Sharing.Confirm.Remove.Text, {
          advisorName: advisorToRemove ? getAgentFullName(advisorToRemove) : "",
          advisorId: advisorToRemove?.ssoId ?? "",
        })}
        cancelLabel={t(Resources.Common.Cancel)}
        confirmLabel={t(Resources.AnalysisDetail.Sharing.Confirm.Remove.Confirm)}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setAdvisorToRemove(null)}
        isPending={isRemoving}
      />
      <TakeBackConfirmDialog isOpen={isTakeBackConfirmOpen} advisor={sharedWithAgent} onClose={() => setIsTakeBackConfirmOpen(false)} />
    </>
  );
};
